import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ConfirmationModal, { ModalDescription } from './ConfirmationModal'
import styled from '@emotion/styled'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'

const OUTSTANDING_INVOICES_ERROR =
  'Cannot disconnect Stripe while there are outstanding invoices'

interface UseStripeDisconnectArgs {
  disconnect: () => Promise<unknown>
  onSuccess: () => void
  invoicesPath: string
}

export const useStripeDisconnect = ({
  disconnect,
  onSuccess,
  invoicesPath
}: UseStripeDisconnectArgs) => {
  const navigate = useNavigate()
  const { addMessage } = useMessageStore()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isOutstandingInvoicesOpen, setIsOutstandingInvoicesOpen] =
    useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  const handleConfirm = async () => {
    setDisconnecting(true)

    try {
      await disconnect()
      setIsConfirmOpen(false)
      addMessage({ type: 'message', content: 'Stripe account disconnected' })
      onSuccess()
    } catch (error) {
      setIsConfirmOpen(false)
      const errorMessage = error instanceof Error ? error.message : ''

      if (errorMessage === OUTSTANDING_INVOICES_ERROR) {
        setIsOutstandingInvoicesOpen(true)
      } else if (errorMessage === 'Unauthorized') {
        addMessage({
          type: 'error',
          content: 'You are not authorized to disconnect this Stripe account'
        })
      } else {
        addMessage({
          type: 'error',
          content:
            errorMessage ||
            'Failed to disconnect Stripe account. Please try again'
        })
      }
    } finally {
      setDisconnecting(false)
    }
  }

  const modals = (
    <>
      <ConfirmationModal
        title='Are you sure you want to remove your Stripe Connected Account?'
        confirmLabel='Remove'
        isOpen={isConfirmOpen}
        onOk={handleConfirm}
        onCancel={() => setIsConfirmOpen(false)}
        confirmLoading={disconnecting}
      >
        <ModalDescription>
          Without a Stripe Connected account, you won&apos;t be able to take
          orders via Slerp. Please make sure you have a new Connected Account
          set up and ready to connect once your current account has been
          removed.
        </ModalDescription>
        <ModalFootnote>
          Please be aware that any scheduled payouts will be sent to the
          currently connected account, even after this has been removed.
        </ModalFootnote>
      </ConfirmationModal>
      <ConfirmationModal
        title='Outstanding invoices'
        confirmLabel='Go to invoices'
        confirmVariant='default'
        isOpen={isOutstandingInvoicesOpen}
        onOk={() => {
          setIsOutstandingInvoicesOpen(false)
          navigate(invoicesPath)
        }}
        onCancel={() => setIsOutstandingInvoicesOpen(false)}
      >
        <ModalDescription>
          Before you can disconnect your Stripe Connected account, please make
          sure all outstanding invoices are settled.
        </ModalDescription>
        <ModalFootnote>
          Contact{' '}
          <ContactLink href='mailto:finance@slerp.com'>
            finance@slerp.com
          </ContactLink>{' '}
          if you have any questions.
        </ModalFootnote>
      </ConfirmationModal>
    </>
  )

  return { modals, openConfirm: () => setIsConfirmOpen(true), disconnecting }
}

const ModalFootnote = styled(ModalDescription)`
  font-style: italic;
  font-size: 12px;
`

const ContactLink = styled.a`
  color: #2f54eb;
  text-decoration: underline;
`
